<template lang="pug">
  .b-preview(v-if="isOpened" :style="{ 'z-index': zIndex }")
    ._overlay
    ._container(@click="close")
      button._close(type="button" @click="close")
        Icon(name="close3" pointer)
      button._nav.-prev(v-if="hasPrev" type="button" @click.prevent.stop="prev")
        Icon(name="angle-down" pointer)
      button._nav.-next(v-if="hasNext" type="button" @click.prevent.stop="next")
        Icon(name="angle-down" pointer)
      ._spacer(@click.stop)
        img._media(v-if="current.type === 'image'" :src="current.src" :style="shiftStyle" :key="current.src")
        VuePdfEmbed._media(v-else-if="current.type === 'pdf'" :style="`${shiftStyle} width: 85vw;`" :source="`${current.src}?stream=1`")
        video._media(v-else-if="current.type === 'video'" autoplay controls controlslist="nodownload" :style="shiftStyle" :key="current.src")
          source(:src="current.src" type="video/mp4")
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
import Swipe from '@/utils/swipe';
import scrollLock from '@/utils/scrollLock';

/**
 * Полноэкранный просмотр изображений, видео и PDF с листанием.
 *
 * Открывается через popupModel.addPreview(items, index): живёт в
 * PopupDispatcher у корня страницы, а не в разметке открывшего компонента.
 * Это принципиально: у боковой шторки (BaseDrawer) есть transform, и
 * position: fixed внутри неё считается от самой шторки — просмотр, отрисованный
 * из её содержимого, оверлеем накрывал бы только её.
 *
 * items — [{ type: 'image' | 'video' | 'pdf', src }]. Промис open()
 * разрешается при закрытии.
 */
export default {
  name: 'BasePreview',
  components: { VuePdfEmbed },
  controller: null,
  data() {
    return {
      isOpened: false,
      items: [],
      index: 0,
      xShift: 0,
      zIndex: 10000,
    };
  },
  computed: {
    current() { return this.items[this.index] ?? {}; },
    hasPrev() { return this.index > 0; },
    hasNext() { return this.index < this.items.length - 1; },
    shiftStyle() { return `translate: ${this.xShift}px 0px;`; },
    swipe() { return new Swipe(this.onSwipe, this.onMove); },
  },
  watch: {
    isOpened(opened) {
      if (opened) this.attach();
      else this.detach();
    },
  },
  // диспетчер может убрать просмотр снаружи (popupModel.resolve) — watcher
  // при уничтожении уже не сработает, отпускаем страницу здесь
  beforeDestroy() {
    if (this.isOpened) this.detach();
  },
  methods: {
    // не пишем в body.style напрямую: просмотр открывается и поверх попапа,
    // и поверх шторки — разблокировать страницу должен только последний
    // закрывшийся (см. utils/scrollLock)
    attach() {
      scrollLock.lock();
      document.addEventListener('keyup', this.onKeyUp);
      this.swipe.subscribe();
    },
    detach() {
      scrollLock.unlock();
      document.removeEventListener('keyup', this.onKeyUp);
      this.swipe.unsubscribe();
    },
    open(items, index = 0) {
      this.items = items;
      this.index = Math.min(Math.max(index, 0), Math.max(items.length - 1, 0));
      this.xShift = 0;
      this.isOpened = true;

      return new Promise((resolve, reject) => {
        this.$options.controller = { resolve, reject };
      });
    },
    close() {
      this.isOpened = false;
      this.$options.controller.resolve();
    },
    prev() {
      if (this.hasPrev) this.index -= 1;
    },
    next() {
      if (this.hasNext) this.index += 1;
    },
    onSwipe(dir) {
      switch (dir) {
        case 'left': this.prev(); break;
        case 'right': this.next(); break;
        default: break;
      }
    },
    onMove(x) {
      this.xShift = x;
    },
    // Escape не обрабатываем: шторка и попап под просмотром слушают его на
    // document тоже, и одно нажатие закрыло бы всё разом
    onKeyUp(e) {
      switch (e.key) {
        case 'ArrowLeft': this.prev(); break;
        case 'ArrowRight': this.next(); break;
        default: break;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.preview {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
  }

  &__container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: auto;
    cursor: pointer;
  }

  &__spacer {
    margin: auto;
    cursor: default;
  }

  &__media {
    max-width: 90vw;
    max-height: 90vh;
    margin: auto;
  }

  &__nav {
    position: fixed;
    top: 0;
    bottom: 0;
    width: 10vw;
    display: flex;
    justify-content: center;
    align-items: center;
    background: transparent;
    cursor: pointer;

    svg {
      width: 100px;
      height: auto;

      &::v-deep path {
        stroke: #767676;
      }
    }

    &--next {
      right: 0;
      svg {
        transform: rotate(-90deg);
      }
    }

    &--prev {
      left: 0;
      svg {
        transform: rotate(90deg);
      }
    }
  }

  &__close {
    width: 10vw;
    height: 200px;
    position: absolute;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    background: transparent;
    cursor: pointer;

    svg {
      width: 30px;
      height: 30px;

      &::v-deep path {
        stroke: #767676;
      }
    }
  }
}
</style>
